/**
 * Classic main screen login form (`main_screen_variant: classic`).
 * Tune this file only for classic — hero_dock has its own copy under `variants/hero_dock/`.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, Text, ScrollView, Alert } from 'react-native';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { Clickable2 } from '../../../components/common/Clickable2';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { Input } from '../../../components/common/Input';
import { appTheme, useThemeColors } from '../../../shared/theme';
import { requestPasswordReset } from '../../../data/domains/profile';
import { finalizeLoginSuccess, signInWithPhonePassword, enrichBlockingSigninOtpParams, getAuthStore, persistSessionUser, setLoginJustHappened } from '../../../shared/auth';
import { getFcmDeviceTokenForAuth } from '../../../platform/notifications/fcmDeviceToken';
import { useOrganizationSettings, resolvePhoneMaxDigits } from '../../../data/domains/config';
import {
  authenticateWithBiometrics,
  hasStoredCredentials,
  isDeviceBiometryReady,
  loadStoredCredentials,
} from '../../../shared/auth';
import { useTranslation } from '../../../shared/i18n';
import { showErrorAlert, showSuccessAlert } from '../../../components/appAlerts';
import { useAsyncWithLoading } from '../../../components/ui/hooks/useAsyncWithLoading';
import { sharedStyles, createSolidPrimaryCtaLabelStyle } from '../../../shared/theme/sharedStyles';
import { createCredentialFormSheetStyles, createCredentialPrimaryCtaSurfaceStyle } from '../../../shared/theme/styles/sheets';
import { useAuthSheets } from '../../../shared/auth/authSheetContext';
import { getDevLoginPasswordInitial, getDevLoginPhoneInitial } from '../../../exDev';
import { authSheetFormSpacing, AUTH_FORM_KEYBOARD, useAuthFormScrollMaxHeight } from '../../../shared/auth';
import { useAuthInputKeyboardAvoidance } from '../../keyboard/useAuthInputKeyboardAvoidance';
import { AuthFocusTarget } from '../../keyboard/AuthFocusTarget';
import type { User } from '../../../data/domains/profile';

export type LoginSheetContentProps = {
  onDismiss: () => void;
  /** Extra auth modal lift when scroll cannot clear the focused field (overlap only, never full keyboard height). */
  onKeyboardPanelNudge?: (offset_px: number) => void;
};

export function LoginSheetContent({ onDismiss, onKeyboardPanelNudge }: LoginSheetContentProps) {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createCredentialFormSheetStyles(colors), [colors]);
  const scroll_max_h = useAuthFormScrollMaxHeight();
  const { openRegister, openReset, openOtp } = useAuthSheets();
  const {
    settings: { allowedDigitForPhone: userPhoneAllowedDigit, dialing_code: dialing_code, country_iso_code: country_iso_from_settings },
  } = useOrganizationSettings();

  /** When initial config is not yet loaded (e.g. after logout), org `dialing_code` can be missing — match Register fallback. */
  const display_dialing_code = dialing_code != null ? String(dialing_code) : '965';
  const phone_max_digits = resolvePhoneMaxDigits(userPhoneAllowedDigit, display_dialing_code);

  const [phone, setPhone] = useState(getDevLoginPhoneInitial());
  const [password, setPassword] = useState(getDevLoginPasswordInitial());
  const [loading, runAsync] = useAsyncWithLoading();
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      const has_creds = await hasStoredCredentials();
      const hardware_ok = await isDeviceBiometryReady();
      setBiometricAvailable(has_creds && hardware_ok);
    } catch {
      setBiometricAvailable(false);
    }
  };

  const onPhoneInput = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    setPhone(cleaned.slice(0, phone_max_digits));
    if (loginError) {
      setLoginError(null);
    }
  };

  const handleBiometricLogin = async () => {
    await runAsync(async () => {
      const authenticated = await authenticateWithBiometrics({
        title: t('auth.biometricPromptTitle'),
        subtitle: t('auth.biometricPromptSubtitle'),
      });
      if (!authenticated) {
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableShort'));
        return;
      }
      const credentials = await loadStoredCredentials();
      if (!credentials) {
        Alert.alert(t('auth.biometricUnavailable'), t('auth.biometricUnavailableMessage'));
        setBiometricAvailable(false);
        return;
      }
      await handleLogin(credentials.username, credentials.password);
    }).catch((error: any) => {
      showErrorAlert(t, { message: error.message || t('auth.unexpectedError') });
    });
  };

  const completeLoginSuccess = useCallback(
    async (customer: User, loginPhone: string, loginPassword: string) => {
      const session_options = {
        defer: 'none' as const,
      };
      await finalizeLoginSuccess({
        user: customer,
        onDismiss,
        session_options,
        login_phone: loginPhone,
        login_password: loginPassword,
        offer_biometric_save: true,
        t,
      });
    },
    [onDismiss, t],
  );

  const showLoginFailure = useCallback(
    (message: string) => {
      const resolved = message.trim() || t('auth.loginFailed');
      setLoginError(resolved);
      showErrorAlert(t, { message: resolved });
    },
    [t],
  );

  const handleLogin = async (phoneOverride?: string, passwordOverride?: string) => {
    const loginPhone = phoneOverride || phone;
    const loginPassword = passwordOverride || password;

    if (!loginPhone || !loginPassword) {
      showLoginFailure(t('auth.phoneAndPasswordRequired'));
      return;
    }

    setLoginError(null);
    await runAsync(async () => {
      const device_token = await getFcmDeviceTokenForAuth();
      const r = await signInWithPhonePassword(loginPhone, loginPassword, { device_token, skipStoreUpdate: true });

      if (!r.ok) {
        showLoginFailure(r.message || t('auth.loginFailed'));
        return;
      }
      if (r.unverified) {
        await persistSessionUser(r.user);
        getAuthStore().setUser(r.user);
        setLoginJustHappened();
        onDismiss();
        openOtp(
          enrichBlockingSigninOtpParams({
            phone: loginPhone,
            countryCode: `+${display_dialing_code}`,
            redirectTo: 'MainTabs',
            loginData: { phone: loginPhone, password: loginPassword },
          }),
        );
        return;
      }
      await completeLoginSuccess(r.user, loginPhone, loginPassword);
    }).catch((error: unknown) => {
      showLoginFailure(error instanceof Error ? error.message : t('auth.unexpectedError'));
    });
  };

  const handleForgotPassword = async () => {
    if (!phone) {
      showErrorAlert(t, { message: t('auth.enterPhoneFirst') });
      return;
    }

    await runAsync(async () => {
      const response: any = await requestPasswordReset(phone);

      if (response.success) {
        showSuccessAlert(t, { message: response.message || t('auth.resetCodeSent') });
        openReset({
          phoneNo: phone,
          country_iso_code: country_iso_from_settings ?? 'KW',
          redirectTo: 'Login',
          historyRoute: 'Login',
        });
      } else {
        showErrorAlert(t, { message: response.message || t('auth.passwordResetFailed') });
      }
    }).catch((error: any) => {
      showErrorAlert(t, { message: error.message || t('auth.unexpectedError') });
    });
  };

  const navigateToSignup = () => openRegister({ presentation: 'default' });

  const scroll_ref = useRef<ScrollView>(null);
  const scroll_y = useRef(0);
  const phone_block_ref = useRef<View>(null);
  const password_block_ref = useRef<View>(null);
  const { on_field_focus, on_scroll_layout, on_scroll_content_size_change, keyboard_scroll_pad } = useAuthInputKeyboardAvoidance({
    enabled: true,
    scroll_ref,
    scroll_y_ref: scroll_y,
    on_panel_offset: onKeyboardPanelNudge,
    clearance_px: AUTH_FORM_KEYBOARD.fieldFocusClearancePx,
  });

  const auth_primary_cta = useMemo(
    () => ({
      surface: createCredentialPrimaryCtaSurfaceStyle(colors),
      label: createSolidPrimaryCtaLabelStyle(colors),
    }),
    [colors],
  );

  const scroll_body = (
    <ScrollView
      ref={scroll_ref}
      onScroll={e => {
        scroll_y.current = e.nativeEvent.contentOffset.y;
      }}
      onLayout={on_scroll_layout}
      onContentSizeChange={on_scroll_content_size_change}
      scrollEventThrottle={16}
      style={[styles.scroll, { maxHeight: scroll_max_h }]}
      contentContainerStyle={[
        styles.scrollContent,
        {
          paddingTop: authSheetFormSpacing.formPaddingTop,
          paddingBottom: authSheetFormSpacing.formPaddingBottom + keyboard_scroll_pad,
        },
      ]}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      showsVerticalScrollIndicator={false}
      automaticallyAdjustKeyboardInsets={false}
      bounces={false}>
      <View style={styles.fields}>
        <AuthFocusTarget ref={phone_block_ref}>
          <Input
            label={t('auth.phoneNumber')}
            value={phone}
            onChangeText={onPhoneInput}
            placeholder={t('auth.phonePlaceholder')}
            keyboardType="phone-pad"
            maxLength={phone_max_digits}
            containerStyle={sharedStyles.inputContainerFlush}
            leftAccessory={<Text style={styles.countryCodeText}>+{display_dialing_code}</Text>}
            onFocus={() => on_field_focus(phone_block_ref)}
          />
        </AuthFocusTarget>
        <AuthFocusTarget ref={password_block_ref}>
          <Input
            label={t('auth.password')}
            value={password}
            onChangeText={text => {
              setPassword(text);
              if (loginError) {
                setLoginError(null);
              }
            }}
            placeholder={t('auth.enterPassword')}
            secureTextEntry
            containerStyle={sharedStyles.inputContainerFlush}
            onFocus={() => on_field_focus(password_block_ref)}
          />
        </AuthFocusTarget>
        {loginError ? (
          <Text style={[styles.signupText, { color: colors.danger, marginTop: appTheme.spacing.xxs }]} accessibilityLiveRegion="polite">
            {loginError}
          </Text>
        ) : null}
      </View>

      <Clickable2
        testID="signin-forgot-password"
        onPress={handleForgotPassword}
        haptics
        hapticType="selection"
        accessibilityRole="link"
        accessibilityLabel={t('auth.forgotPassword')}
        hitSlop={appTheme.hitSlop.small}>
        <View style={styles.forgotActionRoot}>
          <Text style={styles.forgotText}>{t('auth.forgotPassword')}</Text>
        </View>
      </Clickable2>

      <View style={{ marginTop: authSheetFormSpacing.beforePrimaryActions }}>
        <View style={styles.primaryWithBiometricRow}>
          <Clickable2
            testID="signin-continue"
            onPress={() => handleLogin()}
            disabled={loading}
            haptics
            hapticType="impactLight"
            style={styles.primaryCtaRowSlot}
            accessibilityLabel={t('auth.login')}
            accessibilityRole="button"
            accessibilityState={{ disabled: loading }}>
            <View style={[auth_primary_cta.surface, styles.primaryCtaSurfaceFill]}>
              {loading ? <ButtonLoader size={16} color={colors.onPrimary ?? colors.primaryText ?? colors.white} /> : <Text style={auth_primary_cta.label}>{t('auth.login')}</Text>}
            </View>
          </Clickable2>
          <Clickable2
            onPress={handleBiometricLogin}
            disabled={loading}
            haptics
            hapticType="impactLight"
            style={styles.biometricCtaClickable2}
            accessibilityLabel={t('auth.loginWithBiometrics')}
            accessibilityRole="button"
            accessibilityState={{ disabled: loading }}>
            <View style={styles.biometricCtaIconRoot}>
              <SvgIcon name="fingerprint" size={22} color={colors.primary} />
            </View>
          </Clickable2>
        </View>
      </View>

      <View style={{ marginTop: authSheetFormSpacing.afterPrimaryActions }}>
        <View style={[styles.signupRowPad, styles.signupRow]}>
          <Text style={styles.signupText}>{t('auth.dontHaveAccount')}</Text>
          <Clickable2 testID="signin-create-account" onPress={navigateToSignup} haptics hapticType="selection" accessibilityRole="link" accessibilityLabel={t('auth.signUp')} hitSlop={appTheme.hitSlop.small}>
            <View style={styles.signUpActionRoot}>
              <Text style={styles.signupLink}>{t('auth.signUp')}</Text>
            </View>
          </Clickable2>
        </View>
      </View>
    </ScrollView>
  );

  return <View style={styles.keyboardView}>{scroll_body}</View>;
}

export default LoginSheetContent;
